<template>
  <div v-if="!content.hideOnWeb" class="contents w-full">
    <component
      :is="content.target?.url && !content.children?.length ? 'a' : 'button'"
      class="group w-full flex items-center gap-2 py-2 between"
      :class="{
        'op-0 invisible': activeL2?.id,
        'cursor-pointer bg-transparent p-0 text-left': !content.children?.length && isActionLinkOnly(content.target),
      }"
      :href="content.children?.length ? undefined : content.target?.url"
      @click="clickL2(content, $event)"
    >
      <span class="group-hover:-rotate-3" :style="{ 'transition-duration': `${SLANT_TRANSITION_DURATION}ms` }">
        <span class="underscore body-3">
          {{ content.title }}
        </span>
      </span>
      <vf-icon v-if="content.children?.length" name="chevron" size="md" />
    </component>
    <template v-if="content.children?.length">
      <transition name="slide">
        <ul v-show="activeL2?.id === content.id" class="slide-left absolute left-0 top-0 z-1 w-full px-8">
          <li>
            <base-button
              v-show="activeL2?.id"
              :ref="(el) => {
                if (activeTab === Number(content.menuId) && activeL2?.id === content.id)
                  backButtonRef = el as ComponentPublicInstance
              }"
              class="group mb-6 flex items-center gap-2 <md:w-full"
              style="margin-top: -3.055rem"
              @click="activeL2 && (activeL2 = undefined)"
            >
              <vf-icon dir="left" name="chevron" />
              <span class="sr-only">{{ $t.backTo }}</span>
              <span class="underscore subtitle-4">{{ activeL2?.title }}</span>
            </base-button>
          </li>
          <li :class="{ '<lg:hidden': activeL2?.hideShopAll?.sm, 'lg:hidden': activeL2?.hideShopAll?.lg }">
            <base-link v-if="activeL2?.target" class="group w-full flex py-2" :to="activeL2.target.url" @click="clickL2ViewAll(content)">
              <span
                class="group-hover:-rotate-3"
                :style="{ 'transition-duration': `${SLANT_TRANSITION_DURATION}ms` }"
              >
                <span class="underscore body-3">
                  {{ activeL2.overrideShopAll || $t.viewAll }}
                </span>
              </span>
            </base-link>
          </li>
          <li v-for="(l3, l3Key) in visibleChildrenL3" :key="l3Key">
            <cms-section
              :key="l3Key"
              class="h-full children:h-full"
              :section="{ name: l3.id, items: [{ ...l3 }] }"
            />
          </li>
        </ul>
      </transition>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { MegaMenuL1Context } from '#content/contexts/megamenu'
import type { MenuItem } from '#types/components/cms/mega-menu'
import type { RecursivePartial } from '#types/utils'
import { MegaMenuL1ContextKey } from '#content/contexts/megamenu'

const { content } = defineProps<{ content: MenuItem & { menuId: string } }>()

const SLANT_TRANSITION_DURATION = 200

const { activeL1, activeL2, activeTab } = inject(MegaMenuL1ContextKey, {} as MegaMenuL1Context)

const { $t, $gtm, $viewport } = useNuxtApp()
const { getActionLinkHandler, isActionLinkOnly } = useCmsActionLink('header:mega-menu')
const backButtonRef = ref<ComponentPublicInstance | null>()

const visibleChildrenL3 = computed(() => content.children?.filter((item) => !item.hideOnWeb) ?? [])

const getClickEventLabel = (...args: (RecursivePartial<MenuItem> | undefined)[]): string => args
  .map((mi, index) => formatMegaMenuLevelLabel(getMegaMenuItemLabel(mi), (index + 1) as 1 | 2 | 3))
  .filter(Boolean)
  .join(' | ')

const clickL2 = async (l2: MenuItem, e: MouseEvent) => {
  if (content.children?.length) {
    // Prevent default behavior only when ctrl or cmd key is not pressed
    if (!(e.ctrlKey || e.metaKey)) {
      e.preventDefault()

      // When on mobile, the brand wants the tilted animation to happen, so we hold the transition for the time of the animaiton
      if (!$viewport.lg) await wait(SLANT_TRANSITION_DURATION)

      if (activeL2) {
        activeL2.value = l2

        await nextTick()
        backButtonRef.value?.$el?.focus()

        $gtm.push('topNav.onClickMobileMenuSection', getClickEventLabel(activeL1?.value, l2))
      }
    }
    else {
      $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1?.value, l2))
    }
  }
  else {
    e.preventDefault()
    const actionHandler = getActionLinkHandler(l2.target, getMegaMenuItemLabel(l2))
    if (actionHandler) actionHandler()
    else navigateTo(l2?.target?.url)
    $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1?.value, l2))
  }
}

const clickL2ViewAll = (l2: MenuItem) => {
  $gtm.push('topNav.onClickTopNavLink', getClickEventLabel(activeL1?.value, l2))
}
</script>
